import { Report } from '../types';
import { isMaintenanceWriteError } from './maintenanceLogic';
import { isBuildGuardWriteError } from './versionGate';
import { istEigenerNetzFehler, istNetzFehler } from './netzFehler';

// Pure Zustands- und Retrylogik der Offline-Sync-Queue.
// Bewusst ohne DOM-, IndexedDB- oder Supabase-Abhängigkeit (testbar ohne jsdom);
// Uhr und Zufall werden injiziert. Siehe docs/superpowers/specs/2026-07-23-*.md.

export type QueueState = 'pending' | 'syncing' | 'blocked';

/**
 * 'build-veraltet' = der Server hat abgewiesen, weil DIESES Bundle zu alt ist
 * (Build-Riegel, Signalwort BUILD_VERALTET). Kein Fehler des Eintrags und
 * nichts, was eine Wiederholung heilt — er wartet auf den Reload.
 *
 * 'abgewiesen' = die Datenbank hat den INHALT abgelehnt (Datentyp, Eindeutigkeit,
 * Rechte, Riegel — siehe `istDatenbankAbsage`). Dieselbe Eingabe scheitert bei
 * jedem Versuch gleich; wiederholt wird erst mit neuem Inhalt oder beim
 * nächsten Start der App.
 */
export type SyncFailureKind = 'retryable' | 'auth-required' | 'blocked' | 'storage-error' | 'build-veraltet' | 'abgewiesen';

/**
 * Alle Zustände, die diese Version kennt. Bestandsdaten aus älteren Versionen
 * können andere tragen (z.B. das abgeschaffte 'conflict') — siehe
 * normalizeQueueEntry.
 */
const KNOWN_STATES: readonly string[] = ['pending', 'syncing', 'blocked'];

export interface QueueEntry {
    schemaVersion: 1;
    /**
     * Die Identität DIESES Warteschlangeneintrags — einmal vergeben, nie
     * wiederverwendet.
     *
     * `rev` reicht dafür nicht: sie zählt je Eintrag ab 0, und nach einem
     * Löschen beginnt der nächste Eintrag desselben Projekts wieder bei 0.
     * Genau daran hing ein Verlustpfad: Tab A lädt rev 0 hoch (langsam), Tab B
     * gibt den hängenden Eintrag frei, lädt ihn hoch und löscht ihn, der
     * Anwender tippt weiter und legt einen NEUEN Eintrag mit rev 0 an — und
     * der verspätete Abschluss von Tab A löschte diese nie hochgeladene
     * Eingabe, weil 0 gleich 0 ist. Mit der Identität trifft er ins Leere.
     *
     * Optional, weil Bestandsdaten aus älteren Builds sie nicht tragen;
     * `normalizeQueueEntry` vergibt sie beim Lesen, der nächste Schreibvorgang
     * hält sie fest.
     */
    entryId?: string;
    userId: string;
    projectId: string;
    report: Report;
    /**
     * Der Stand, auf dem dieser Eintrag AUFSETZT — festgehalten beim ersten
     * Einreihen und danach nie wieder verändert.
     *
     * Ohne ihn lässt sich beim Hochladen nicht unterscheiden, ob eine im
     * `report` fehlende Zeile ENTFERNT wurde oder nie gesehen wurde. Genau
     * diese Unterscheidung entscheidet, ob eine fremde Buchung erhalten bleibt
     * oder verschwindet (src/services/reportMerge.ts). Alteinträge aus
     * Versionen vor dem 31.08.2026 tragen ihn nicht; für die gibt es die
     * Bergung.
     */
    base?: Report;
    /** `projects.updated_at` von `base` — der Anker dieses Eintrags. */
    baseUpdatedAt?: string | null;
    queuedAt: string;
    updatedAt: string;
    retryCount: number;
    nextRetryAt: string | null;
    state: QueueState;
    /**
     * Wann das Hochladen begonnen hat — nur im Zustand 'syncing' gesetzt.
     *
     * Optional, weil Bestandsdaten aus älteren Builds ihn nicht tragen; für
     * die Freigabe verwaister Einträge zählt genau das als „unbekannt alt"
     * (istVerwaisterSync).
     */
    syncStartedAt?: string;
    /**
     * Die Identität des LAUFENDEN Hochladens — je Start neu, nur im Zustand
     * 'syncing' gesetzt.
     *
     * `entryId` allein reicht nicht: Tab A startet, bleibt hängen; nach fünf
     * Minuten gibt Tab B denselben Eintrag frei und startet ihn erneut; A
     * meldet sich verspätet mit einem 403 — und setzte über den
     * Fehlerabschluss Bs laufenden Lauf auf 'blocked', denn Eintrag und
     * Revision stimmen ja. Mit der Lauf-Identität trifft A ins Leere.
     */
    flushId?: string;
    // Monotone Revision: identifiziert exakt den geflushten Stand — ein
    // Zeitstempelvergleich reicht nicht (zwei Enqueues im selben ms-Tick).
    rev: number;
    lastErrorCode?: string;
    lastErrorMessage?: string;
    /**
     * Die Revision, für die der Altersriegel dem Anwender schon einmal
     * gemeldet wurde — PERSISTENT, nicht nur für die Laufzeit dieses Tabs.
     *
     * Ohne dieses Feld hing das Gedächtnis allein an einem React-Ref
     * (useOfflineSync.announcedTooOldRef) und war mit jedem Neustart weg: der
     * Balken „Nicht übertragen …" stand jeden Morgen und nach jedem Reload
     * erneut da, für einen Eintrag, der sich nie wieder ändert. Wer das
     * betroffene Projekt nie wieder anfasst, bekam ihn bis ans Ende der Tage.
     *
     * Optional, weil Bestandsdaten aus älteren Builds es nicht tragen — die
     * werden dann einmal gemeldet und danach festgeschrieben. An `rev`
     * gebunden und damit selbstreinigend: ein neuer Bearbeitungsgang zählt
     * `rev` hoch (coalesceReportSave ersetzt einen angehaltenen Eintrag
     * ohnehin vollständig), und der nächste Riegel meldet sich wieder.
     */
    zuAltGemeldetRev?: number | null;
}

export interface IncomingSave {
    userId: string;
    projectId: string;
    report: Report;
    /** Der Serverstand, aus dem dieser Bearbeitungsgang hervorgegangen ist. */
    base?: Report;
    baseUpdatedAt?: string | null;
}

/**
 * Eine Identität, die es nur einmal gibt.
 *
 * `crypto.randomUUID` ist der Normalfall; der Rückfall deckt alte WebViews
 * (und unsichere Herkünfte, wo `crypto` fehlen kann) ab. Er muss nicht
 * kryptografisch sein — er muss nur zwei Einträge desselben Projekts
 * auseinanderhalten, und die entstehen Sekunden bis Tage auseinander.
 */
export const erzeugeEintragsId = (): string => {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
    } catch {
        // Weiter zum Rückfall.
    }
    return 'q-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
};

/**
 * Bringt einen aus der IndexedDB gelesenen Eintrag auf den Stand DIESER
 * Version.
 *
 * Pflicht, kein Komfort: auf den Geräten der Mitarbeiter liegen Einträge aus
 * älteren Versionen, unter anderem im abgeschafften Zustand 'conflict'. Kennt
 * niemand mehr diesen Zustand, flusht `canFlush` sie nie wieder — die Arbeit
 * dieser Person hinge für immer fest, ohne jede Meldung. Ein unbekannter
 * Zustand wird deshalb zu 'pending', und der Wartetimer fällt weg, damit der
 * nächste Flush-Trigger sie sofort mitnimmt.
 */
/**
 * ACHTUNG: die hier vergebene Identität ist FLÜCHTIG, solange sie nicht
 * geschrieben wurde — zwei Lesevorgänge desselben Altbestands liefern zwei
 * verschiedene. Einen Abschluss autorisieren darf deshalb nur die
 * PERSISTIERTE Kennung aus `starteFlush`.
 */
export const normalizeQueueEntry = <T extends QueueEntry | undefined>(entry: T): T => {
    if (!entry) return entry;
    const zustandOk = KNOWN_STATES.includes(entry.state);
    if (zustandOk && entry.entryId) return entry;
    return {
        ...entry,
        // Bestandsdaten ohne Identität bekommen hier eine; festgehalten wird
        // sie mit dem nächsten Schreibvorgang (markSyncStart, coalesce …).
        entryId: entry.entryId ?? erzeugeEintragsId(),
        ...(zustandOk ? {} : { state: 'pending' as QueueState, nextRetryAt: null }),
    } as T;
};

/**
 * ═══ Altersriegel ══════════════════════════════════════════════════════════
 *
 * Am 07.09.2026 haben Warteschlangeneinträge, die Stunden bis Tage alt waren,
 * beim Wiederauftauchen der Geräte alte Begehungszähler über frische
 * Buchungen geschrieben — 24 Projekte mussten per SQL repariert werden. Anker
 * und Bergung fangen davon einen Teil, aber ein tagealter Eintrag hat schlicht
 * nichts mehr, was das Hochladen wert wäre: was er trägt, hat der Nutzer
 * längst am Rechner nachgezogen oder verworfen. Er bleibt reines Risiko.
 *
 * Deshalb: älter als 24 Stunden wird NICHT hochgeladen, sondern in 'blocked'
 * gestellt, mit Code 'zu-alt' und einer Meldung, die Projekt und Alter nennt.
 * Nicht gelöscht — der Eintrag ist der Beleg dafür, was liegen geblieben ist.
 *
 * Gemessen wird am INHALT, also an `updatedAt`, nicht an `queuedAt`:
 * `coalesceReportSave` ersetzt den Report bei jedem Speichern vollständig,
 * `updatedAt` ist damit exakt das Alter dessen, was hochgeladen würde.
 * `queuedAt` beschreibt nur das Alter des Ankers (`base`) — und einen alten
 * Anker behandelt das Zusammenführen bereits. Wer gestern eingereiht und
 * eben noch einmal gespeichert hat, lädt hoch; wer seit drei Tagen nicht
 * mehr gespeichert hat, nicht.
 */
export const MAX_QUEUE_AGE_MS = 24 * 60 * 60 * 1000;

/** `lastErrorCode` eines vom Altersriegel angehaltenen Eintrags. */
export const TOO_OLD_ERROR_CODE = 'zu-alt';

/** `lastErrorCode` eines Eintrags, den der Build-Riegel des Servers abgewiesen hat. */
export const BUILD_OUTDATED_ERROR_CODE: SyncFailureKind = 'build-veraltet';

/** `lastErrorCode` eines Eintrags, dessen Inhalt die Datenbank abgelehnt hat. */
export const ABGEWIESEN_ERROR_CODE: SyncFailureKind = 'abgewiesen';

/**
 * Die harte Obergrenze für einen Eintrag, der auf den Reload wartet
 * (`wartetAufReload`).
 *
 * Der Build-Riegel nimmt einen Eintrag vom 24-h-Altersriegel aus, damit die
 * Arbeit einer Nacht nicht verfällt, weil abends ein Mindest-Build gesetzt
 * wurde. Ohne Obergrenze galt das aber für IMMER: ein Gerät, das wochenlang
 * nicht neu lädt (PWA im Hintergrund, Tablet im Schrank), hätte beim ersten
 * Reload einen wochenalten Stand hochgeladen — genau die Klasse vom
 * 07.09.2026, nur durch eine andere Tür. 72 Stunden decken Freitagabend bis
 * Montagmorgen; was älter ist, wird angehalten wie jeder andere Eintrag.
 */
export const MAX_RELOAD_WAIT_MS = 72 * 60 * 60 * 1000;

/**
 * Wartet dieser Eintrag auf den Reload, statt zu altern?
 *
 * Der Altersriegel stellt nach 24 Stunden ab, was nicht hochgegangen ist —
 * gegen den Fall „Tab seit drei Tagen offen, Stand von vorgestern draufsetzen".
 * Ein vom Build-Riegel abgewiesener Eintrag ist NICHT dieser Fall: er ist
 * frisch, er ist vollstaendig, und er ist ausschliesslich deshalb liegen
 * geblieben, weil der Server dieses PROGRAMM abgewiesen hat. Nach dem Reload
 * nimmt ihn der neue Build sofort mit — mit Anker und Zusammenfuehrung, also
 * mit genau den Schutzmechanismen, um derentwillen der Riegel gesetzt wurde.
 *
 * Wuerde er derweil altern, ginge die Arbeit einer Nacht verloren, weil ein
 * Administrator abends auf einen Knopf gedrueckt hat.
 *
 * Der Merker ist selbstreinigend: scheitert der naechste Versuch aus einem
 * ANDEREN Grund, ueberschreibt `markSyncFailure` den Code, und der Eintrag
 * altert wieder ganz normal.
 *
 * Die Ausnahme ist begrenzt: nach `MAX_RELOAD_WAIT_MS` greift der
 * Altersriegel trotzdem (`istZuAlt`).
 */
export const wartetAufReload = (entry: QueueEntry): boolean =>
    entry.lastErrorCode === BUILD_OUTDATED_ERROR_CODE;

/** Alter des hochzuladenden Inhalts in ms; unlesbare Zeitstempel zählen als 0 (kein Riegel auf Verdacht). */
export const queueEntryAgeMs = (entry: QueueEntry, nowIso: string): number => {
    const stand = new Date(entry.updatedAt ?? entry.queuedAt).getTime();
    const now = new Date(nowIso).getTime();
    if (!Number.isFinite(stand) || !Number.isFinite(now)) return 0;
    return Math.max(0, now - stand);
};

export const istZuAlt = (
    entry: QueueEntry,
    nowIso: string,
    maxAgeMs: number = MAX_QUEUE_AGE_MS,
    maxReloadWaitMs: number = MAX_RELOAD_WAIT_MS,
): boolean =>
    queueEntryAgeMs(entry, nowIso) > (wartetAufReload(entry) ? Math.max(maxAgeMs, maxReloadWaitMs) : maxAgeMs);

/** Ein Eintrag, den der Altersriegel angehalten hat (und kein Rechte-/Validierungsfehler). */
export const istZuAltBlockiert = (entry: QueueEntry): boolean =>
    entry.state === 'blocked' && entry.lastErrorCode === TOO_OLD_ERROR_CODE;

/**
 * Festhalten, dass die Absage des Altersriegels für GENAU diesen Stand
 * gemeldet wurde. Nur der Merker wird gesetzt — Zustand, Inhalt, Anker,
 * Fehlerspur und `rev` bleiben unangetastet; der Eintrag ist weiterhin der
 * Beleg dessen, was liegen geblieben ist.
 */
export const markTooOldAnnounced = (entry: QueueEntry): QueueEntry =>
    ({ ...entry, zuAltGemeldetRev: entry.rev });

/**
 * Wurde dieser angehaltene Eintrag in DIESEM Stand schon gemeldet?
 *
 * Drei Bedingungen, und alle drei zählen: Bestandsdaten ohne das Feld gelten
 * als ungemeldet (`undefined === rev` ist falsch), ein Eintrag, der aus einem
 * anderen Grund blockiert ist, gehört nicht hierher, und ein Merker aus einer
 * älteren Revision zählt nicht — der Inhalt ist dann ein anderer.
 */
export const istZuAltGemeldet = (entry: QueueEntry): boolean =>
    istZuAltBlockiert(entry) && entry.zuAltGemeldetRev === entry.rev;

/**
 * Darf der lokale Stand dieses Eintrags den Serverstand verdrängen?
 *
 * App.tsx zieht beim Öffnen und auf dem Rückweg nach einer Sperre einen
 * Warteschlangeneintrag dem Serverstand vor („das Neueste, was es von uns
 * gibt"). Für einen vom Altersriegel angehaltenen Eintrag gilt das gerade
 * nicht: flösse er in den State, ginge er mit dem nächsten Tastendruck
 * durch die Hintertür doch hoch — der Riegel wäre nur eine Verzögerung.
 *
 * Und ebenso wenig für einen, der zu alt IST, aber noch nicht angehalten
 * wurde (Lücke O1, 21.10.2026): angehalten wird ein Eintrag erst beim
 * Hochladen (useOfflineSync.flushProject). Wer das Projekt öffnet, bevor die
 * Warteschlange ihn angefasst hat, bekam bis dahin den wochenalten Stand als
 * Entwurf in den State — Positionen und Notizen aus dem alten Stand, Anker
 * und Ausgangsstand frisch vom Server. Das nächste Speichern traf den Anker
 * und glich die Kindtabellen gegen die alte Liste ab (6223, 5882, 6185).
 * Deshalb zählt das ALTER hier selbst, nicht erst die Markierung.
 */
export const lokalerStandZaehlt = (entry: QueueEntry | undefined, nowIso: string): entry is QueueEntry =>
    !!entry && !istZuAltBlockiert(entry) && !istZuAlt(entry, nowIso);

/** Ein Eintrag, dessen Inhalt die Datenbank abgelehnt hat — wartet auf neuen Inhalt oder den nächsten Start. */
export const istAbgewiesen = (entry: QueueEntry): boolean =>
    entry.state === 'blocked' && entry.lastErrorCode === ABGEWIESEN_ERROR_CODE;

/**
 * Darf der Altersriegel diesen Eintrag anhalten?
 *
 * 'pending' wie bisher; dazu ein abgewiesener — der wartet auf einen
 * erneuten Versuch, und den bekommt er nach 24 Stunden nicht mehr.
 * Nicht: 'syncing' (ein anderer Tab lädt gerade hoch; ein verwaister Lauf
 * wird erst freigegeben und dann angehalten) und kein anders blockierter
 * Eintrag (der wartet auf eine Entscheidung, die hier niemand trifft).
 */
export const darfAngehaltenWerden = (entry: QueueEntry): boolean =>
    entry.state === 'pending' || istAbgewiesen(entry);

/**
 * Was der Balken zu einem abgewiesenen Eintrag sagt — Projekt, Grund der
 * Datenbank, und was weiter geschieht.
 */
export const abgewiesenMeldung = (entry: QueueEntry): string => {
    const grund = entry.lastErrorMessage?.trim();
    return `Nicht übertragen: Der Server hat Ihre Änderung am Projekt „${queueEntryProjectLabel(entry)}" `
        + `abgelehnt${grund ? ` (${grund})` : ''}. Sie bleibt auf diesem Gerät gesichert und wird mit Ihrer `
        + `nächsten Änderung an diesem Projekt oder beim nächsten Start der App erneut gesendet. `
        + `Bleibt die Meldung, bitte an die Administration wenden.`;
};

/**
 * Einen abgewiesenen Eintrag noch einmal versuchen lassen (beim Start der
 * App: ein neuer Programmstand behebt den Grund womöglich). Nur der Zustand
 * wandert; Inhalt, Anker, `rev` und `updatedAt` bleiben — der Altersriegel
 * misst weiter am Inhalt.
 */
export const abgewiesenErneutVersuchen = (entry: QueueEntry): QueueEntry =>
    ({ ...entry, state: 'pending', nextRetryAt: null });

const formatQueueAge = (ms: number): string => {
    const stunden = Math.floor(ms / 3_600_000);
    if (stunden < 1) return `${Math.max(1, Math.floor(ms / 60_000))} Minuten`;
    if (stunden < 48) return `${stunden} Stunden`;
    return `${Math.floor(stunden / 24)} Tage`;
};

/** Projektbezeichnung für Meldungen — aus dem Report, sonst die ID. */
export const queueEntryProjectLabel = (entry: QueueEntry): string => {
    const meta = (entry.report as { metadata?: { projectNumber?: unknown; projectName?: unknown } } | undefined)?.metadata;
    const nummer = typeof meta?.projectNumber === 'string' ? meta.projectNumber.trim() : '';
    const name = typeof meta?.projectName === 'string' ? meta.projectName.trim() : '';
    const teile = [nummer, name].filter(Boolean);
    return teile.length > 0 ? teile.join(' – ') : entry.projectId;
};

/**
 * Stellt einen zu alten Eintrag ab: 'blocked', kein Timer, Code 'zu-alt'.
 * Inhalt, Anker und `rev` bleiben unangetastet — der Eintrag soll lesbar
 * bleiben, und `rev` identifiziert ihn für die einmalige Meldung.
 */
export const markTooOld = (entry: QueueEntry, nowIso: string): QueueEntry => {
    const alter = formatQueueAge(queueEntryAgeMs(entry, nowIso));
    return {
        ...entry,
        state: 'blocked',
        nextRetryAt: null,
        lastErrorCode: TOO_OLD_ERROR_CODE,
        lastErrorMessage:
            `Nicht übertragen: Ihre lokale Änderung am Projekt „${queueEntryProjectLabel(entry)}" `
            + `ist ${alter} alt und wurde deshalb nicht hochgeladen. Der aktuelle Stand des Projekts `
            + `stammt vom Server; bitte prüfen Sie dort, ob etwas nachzutragen ist.`,
    };
};

export const coalesceReportSave = (
    existing: QueueEntry | undefined,
    incoming: IncomingSave,
    nowIso: string,
    neueId: () => string = erzeugeEintragsId,
): QueueEntry => {
    // Ein vom Altersriegel angehaltener Eintrag wird beim nächsten Speichern
    // nicht fortgeschrieben, sondern ERSETZT: sein Inhalt wurde gerade für
    // wertlos erklärt, und sein Anker ist Tage alt. Bliebe der Riegel wie bei
    // Rechte-/Validierungsfehlern stehen, hinge frische Arbeit hinter einer
    // Sperre, die niemand lösen kann. Nur `rev` läuft weiter — die einmalige
    // Meldung hängt daran. Der Merker `zuAltGemeldetRev` fällt dabei von
    // selbst weg: hier entsteht ein NEUES Objekt, das ihn nicht mitnimmt.
    // Genau richtig — meldet der Riegel diesen frischen Stand später erneut,
    // ist das eine neue Nachricht über einen neuen Inhalt.
    //
    // OHNE Ausgangsstand (Lücke O1, 21.10.2026). Bis dahin ging hier der
    // eben geladene Serverstand als `base` mit. Das stimmt nur, wenn der
    // Bericht im State wirklich aus diesem Serverstand stammt — nicht, wenn
    // der alte Eintrag schon als Entwurf im State lag, bevor der Riegel ihn
    // anhielt (Projekt offen, Eintrag altert über die Grenze). Dann trug der
    // ersetzte Eintrag den ALTEN Inhalt mit einem FRISCHEN Ausgangsstand: der
    // Anker traf, der Abgleich der Kindtabellen lief gegen die alte Liste und
    // löschte, was der Server inzwischen führte. Welcher der beiden Fälle
    // vorliegt, ist hier nicht zu sehen. Ohne Ausgangsstand geht der erste
    // Flush durch die Bergung (useOfflineSync.resolveStaleEntry): Protokoll
    // gerettet, Zähler vom Server, Positionen und Notizen nur ergänzt. Danach
    // ist der Eintrag weg, und der nächste setzt auf dem geschriebenen Stand
    // auf (App.handleQueueFlushed).
    if (!existing || istZuAltBlockiert(existing)) {
        const ersetzt = !!existing;
        return {
            schemaVersion: 1,
            // Eine NEUE Identität nur, wenn es wirklich keinen Eintrag gibt.
            // Der angehaltene Eintrag wird zwar inhaltlich ersetzt, bleibt aber
            // derselbe Eintrag in derselben Warteschlange — und `rev` läuft
            // weiter, das hält einen verspäteten Abschluss ohnehin ab.
            entryId: existing?.entryId ?? neueId(),
            userId: incoming.userId,
            projectId: incoming.projectId,
            report: incoming.report,
            base: ersetzt ? undefined : incoming.base,
            baseUpdatedAt: ersetzt ? null : incoming.baseUpdatedAt ?? null,
            queuedAt: nowIso,
            updatedAt: nowIso,
            retryCount: 0,
            nextRetryAt: null,
            state: 'pending',
            rev: existing ? existing.rev + 1 : 0,
        };
    }

    // blocked erfordert eine explizite Entscheidung (Rechte-/Validierungsfehler) —
    // neue Edits heben das nicht auf, aktualisieren aber den Inhalt.
    // Ausnahme: eine Absage der Datenbank galt dem INHALT. Neuer Inhalt ist
    // genau das, was sie heilen kann (Projekttyp zurückgestellt, leeres
    // Datum gefüllt) — er geht wieder in die Warteschlange, mit dem
    // Ausgangsstand des ersten Einreihens.
    const keepFrozenState = existing.state === 'blocked' && !istAbgewiesen(existing);

    // Der Ausgangsstand bleibt der des ERSTEN Einreihens und wird nie
    // nachgezogen. Er beschreibt, wovon die aufgelaufenen Änderungen ausgehen;
    // ihn beim zweiten Speichern durch einen neueren zu ersetzen hieße zu
    // behaupten, alles davor sei nie geändert worden — beim Zusammenführen
    // fiele genau diese Arbeit gegen den Serverstand heraus. Ein Alteintrag
    // ohne Ausgangsstand bekommt aus demselben Grund auch keinen nachträglich.
    return {
        ...existing,
        report: incoming.report,
        updatedAt: nowIso,
        rev: existing.rev + 1,
        state: keepFrozenState ? existing.state : 'pending',
        retryCount: keepFrozenState ? existing.retryCount : 0,
        nextRetryAt: keepFrozenState ? existing.nextRetryAt : null,
    };
};

/**
 * ═══ Gebündeltes lokales Sichern (Entscheid des Betreibers, 30.09.2026) ═══════
 *
 * Bis hierher schrieb JEDER Tastendruck sofort in die IndexedDB
 * (App.handleUpdateReport → useOfflineSync.enqueue → putLatest). Bei Projekt
 * 6051 (639 Positionen) sind das je Anschlag rund vier strukturierte Kopien zu
 * je ~1 MB auf dem Hauptthread — `report` UND `base` beim Lesen und beim
 * Schreiben — plus eine readwrite-Transaktion; beim schnellen Tippen stauten
 * sich die Transaktionen.
 *
 * Deshalb wird gebündelt: geschrieben wird, sobald 300 ms Ruhe ist, beim
 * Durchtippen spätestens alle 2 s, und SOFORT, wenn jemand die Warteschlange
 * liest, hochlädt oder die Seite verlässt (`schreibeJetzt`, die Aufrufstellen
 * stehen in offlineQueue.ts und useOfflineSync.ts). Bewusst hingenommenes
 * Restrisiko: ein harter Absturz kostet die Eingabe seit dem letzten
 * Schreiben — nach einer Tipp-Pause höchstens ~300 ms, beim Durchtippen ohne
 * Pause bis zu 2 s.
 *
 * ─── Generationen, getrennt durch Zugriffe ───────────────────────────────────
 * Je [userId, projectId] gibt es eine Folge von GENERATIONEN. Eine Generation
 * sammelt Aufrufe, bis ein Zugriff auf die Warteschlange kommt (lesen,
 * ändern, löschen — `nachDemWartenden`). Der Zugriff SCHLIESST die laufende
 * Generation jedes Eintrags: ihr Schreiben wird vor ihm angestellt, mit genau
 * dem Inhalt, der bis dahin eingereiht war. Was danach eingereiht wird,
 * beginnt eine neue Generation — mit eigenem Inhalt, eigenem Ausgangsstand
 * (wie beim ersten Einreihen) und eigenem Versprechen —, die erst NACH dem
 * Zugriff geschrieben wird. Ein Zugriff sieht damit genau den Stand von
 * seinem Aufruf, und kein später Eingereihter kann ihn überholen.
 *
 * Innerhalb einer Generation nimmt ein Schreibvorgang den Inhalt erst, wenn
 * er in der Kette dran ist — immer den jüngsten dieser Generation. Eine
 * überholte Wiederholung erledigt sich damit von selbst.
 *
 * Befunde der Prüfung (Codex, 30.09.2026) gegen die Fassungen davor:
 *   • Runde 1: ein direktes putLatest stellte sich erst nach einem `await`
 *     an und überschrieb einen neueren Stand; ein gescheiterter Stand fiel
 *     aus dem Bündel, und das Update hielt die Eingabe für gesichert.
 *   • Runde 2: ein gescheiterter älterer Stand blieb neben einem gesicherten
 *     neueren stehen und schrieb sich später darüber; und beim Anstellen
 *     seiner Wiederholung verschwand er für die Leser.
 *   • Runde 3: putLatest(A1) → removeQueueEntry → putLatest(A2): der erste
 *     Schreibvorgang nahm schon A2 (den damals jüngsten Inhalt), dann löschte
 *     der Zugriff genau diesen Stand, der Satz war aufgeräumt — A2 verloren.
 *
 * Was dabei NICHT nachgeben darf:
 *   • Das Versprechen von `einreihen` löst erst auf, wenn der Stand — oder
 *     ein neuerer DERSELBEN Generation bzw. einer späteren — wirklich in der
 *     IndexedDB steht: derselbe Vertrag wie vorher bei `putLatest`. Aufrufe,
 *     die noch vor dem Beginn eines Schreibvorgangs kamen, teilen sich EIN
 *     Versprechen.
 *   • EINE Kette für alles, angestellt im Augenblick des AUFRUFS.
 *   • Der Ausgangsstand (`base`) ist der des ERSTEN Aufrufs der Generation
 *     bzw. seit Beginn ihres letzten Schreibvorgangs — genau der, den das
 *     sofortige Schreiben beim Anlegen des Eintrags festgehalten hätte
 *     (`coalesceReportSave` nimmt ihn nur beim Anlegen). Scheitert ein
 *     Schreiben, hat es nichts angelegt; dann legt der nächste Aufruf an,
 *     mit SEINEM Ausgangsstand (`basisVomNaechsten`).
 *   • Kommt ein ANDERES Projekt (oder ein anderer Benutzer) herein, wird das
 *     wartende sofort geschrieben: Projekt A liegt fest, bevor B beginnt.
 *   • Ein GESCHEITERTER Stand (Speicher voll, abgebrochene Transaktion)
 *     bleibt als ungesichert stehen, bis ein Schreiben dieses Inhalts oder
 *     eines NEUEREN desselben Eintrags abgeschlossen ist — auch während
 *     seine Wiederholung angestellt ist oder läuft. Solange bleibt
 *     `istBeschaeftigt` true (die Sicherung bleibt in der Registratur
 *     bauphaseFlush.ts angemeldet, das In-App-Update hält die Eingabe nicht
 *     für gesichert), `schreibeJetzt` wirft, und Leser bekommen ihn gezeigt
 *     — aber nur Leser, deren Aufruf NACH ihm kam. Erneut versucht wird beim
 *     nächsten Anlass: ein neuer Anschlag desselben Projekts, jeder Zugriff
 *     auf die Warteschlange, Verdecken/Verlassen der Seite, der
 *     45-s-Durchlauf. Liegt schon eine neuere Generation vor, wird der ältere
 *     gar nicht mehr versucht: die neuere trägt den vollständigen, jüngeren
 *     Bericht und ersetzt ihn.
 *
 * Reine Fabrik ohne DOM und ohne IndexedDB: Schreiber und Uhr werden
 * hereingereicht, damit Fenster, Reihenfolge und Versprechen ohne Browser
 * prüfbar sind.
 */
export const LOKALE_SICHERUNG_RUHE_MS = 300;
export const LOKALE_SICHERUNG_MAX_WARTEN_MS = 2_000;

export interface SchreibbuendelOptionen {
    /** Schreibt einen Stand dauerhaft; löst erst nach abgeschlossener Transaktion auf. */
    schreibe: (save: IncomingSave) => Promise<unknown>;
    setzeUhr: (fn: () => void, ms: number) => unknown;
    loescheUhr: (uhr: unknown) => void;
    ruheMs?: number;
    maxWartenMs?: number;
    /**
     * Meldet jeden Wechsel zwischen „alles liegt fest" (false) und „es wartet,
     * schreibt oder scheiterte noch etwas" (true). Fehler darin werden
     * geschluckt — eine Meldung darf das Sichern nie aufhalten.
     */
    beiBeschaeftigung?: (beschaeftigt: boolean) => void;
}

/** Ein Inhalt, dessen letzter Schreibversuch gescheitert ist — er liegt NUR im Speicher. */
export interface UngesicherterStand {
    save: IncomingSave;
    fehler: unknown;
}

/**
 * Was ein Zugriff von den ungesicherten Ständen sehen darf: nur die, die vor
 * seinem Aufruf eingereiht wurden. Ein später eingereihter Stand gehört zu
 * einer Generation, die erst nach dem Zugriff geschrieben wird.
 */
export interface Zugriffssicht {
    ungesichert: (userId: string, projectId: string) => UngesicherterStand | undefined;
    alleUngesicherten: () => UngesicherterStand[];
    /**
     * NUR nach einem GEGLÜCKTEN ausdrücklichen Löschen des Eintrags
     * (offlineQueue.removeQueueEntry): alle Generationen dieses Eintrags, die
     * VOR dem Zugriff eingereiht waren, verwerfen — auch eine gescheiterte,
     * deren Wiederholung vor dem Löschen noch einmal gescheitert ist. Sonst
     * schriebe der nächste Anlass sie erneut, oder ein Leser bekäme sie als
     * ungesicherten Stand gezeigt: ein eben gelöschter Stand tauchte wieder
     * auf (Prüfung Codex, 30.09.2026, vierte Runde). Später Eingereihtes
     * (eigene Generation nach der Grenze) bleibt und wird danach geschrieben.
     *
     * Wartet dort noch jemand auf ein Versprechen, gilt es als ERFÜLLT: die
     * Eingabe wurde ausdrücklich gelöscht, es gibt nichts mehr, das dauerhaft
     * werden könnte, und „Speicherfehler" wäre die falsche Meldung. In der
     * Praxis wartet dort niemand mehr — jedes Versprechen einer Generation vor
     * der Grenze ist mit ihrem Schreibversuch vor dem Zugriff schon erledigt.
     *
     * Bewusst NICHT für ein Löschen über `mutateQueueEntry` (Abschluss eines
     * Hochladens, Verwerfen eines zu alten Stands): dort ist ein ungesicherter
     * Stand jüngere, nie hochgeladene Eingabe und muss bleiben.
     */
    verwirfBisZurGrenze: (userId: string, projectId: string) => void;
}

export interface Schreibbuendel {
    /** Stand vormerken; das Versprechen löst nach dem dauerhaften Schreiben auf (oder wirft dessen Fehler). */
    einreihen: (save: IncomingSave) => Promise<void>;
    /** Wie `einreihen`, aber ohne Bündelfenster: sofort angestellt. */
    sofort: (save: IncomingSave) => Promise<void>;
    /**
     * Ein Zugriff auf die Warteschlange (lesen, ändern, löschen): schliesst
     * die laufende Generation jedes Eintrags, stellt ihr Schreiben (auch eine
     * Wiederholung) davor an und `arbeit` dahinter.
     */
    nachDemWartenden: <T>(arbeit: (sicht: Zugriffssicht) => Promise<T>) => Promise<T>;
    /** Alles Wartende sofort schreiben und abwarten. Wirft, wenn danach ein Stand ungesichert bleibt. */
    schreibeJetzt: () => Promise<void>;
    /** Wartet, schreibt oder scheiterte noch etwas? */
    istBeschaeftigt: () => boolean;
    /** Der jüngste ungesicherte Inhalt dieses Eintrags, gleich welcher Generation. */
    ungesichert: (userId: string, projectId: string) => UngesicherterStand | undefined;
}

/** Aufrufer, die auf dasselbe Schreiben warten. */
interface Fenster {
    /** Die jüngste Nummer, die in diesem Fenster eingereiht wurde. */
    bis: number;
    /** Noch offen für weitere Aufrufe — bis ein Schreibvorgang beginnt. */
    offen: boolean;
    versprechen: Promise<void>;
    erfuelle: () => void;
    verwirf: (fehler: unknown) => void;
}

/** Eine Generation eines Eintrags (Begründung oben). */
interface Generation {
    /** Fortlaufend über alle Einträge — die Grenze eines Zugriffs ist die höchste bei seinem Aufruf. */
    id: number;
    /** Nimmt noch Aufrufe an — nur die jüngste Generation, bis ein Zugriff sie schliesst. */
    offen: boolean;
    /** Der jüngste Inhalt — was der nächste Schreibvorgang schreibt. */
    save: IncomingSave;
    /** Laufende Nummer dieses Inhalts. */
    nummer: number;
    /** Bis zu welcher Nummer der Inhalt nachweislich in der IndexedDB steht. */
    gesichertBis: number;
    /** Ein Schreibvorgang steht in der Kette und hat noch nicht begonnen. */
    angestellt: boolean;
    /** Ein Schreibvorgang läuft gerade. */
    laeuft: boolean;
    /** Der letzte Versuch ist gescheitert, und seither ist nichts gesichert. */
    gescheitert: boolean;
    fehler: unknown;
    /** Der nächste Aufruf bringt den Ausgangsstand mit (Regel oben). */
    basisVomNaechsten: boolean;
    ruheUhr: unknown;
    maxUhr: unknown;
    fenster: Fenster[];
}

/** Schlüssel eines Warteschlangeneintrags — derselbe wie in der IndexedDB ([userId, projectId]). */
const eintragsSchluessel = (userId: string, projectId: string): string => JSON.stringify([userId, projectId]);

/** Die IndexedDB meldet einen Abbruch mitunter mit `error === null` — weitergereicht wird trotzdem ein Fehler. */
const fehlerOderErsatz = (fehler: unknown): unknown =>
    fehler ?? new Error('Lokale Sicherung fehlgeschlagen');

const neuesFenster = (bis: number): Fenster => {
    let erfuelle: () => void = () => {};
    let verwirf: (fehler: unknown) => void = () => {};
    const versprechen = new Promise<void>((res, rej) => { erfuelle = res; verwirf = rej; });
    // Als behandelt markieren: wer das Versprechen verwirft (`void`), soll
    // keinen „unhandled rejection" auslösen. Wer es abwartet, bekommt den
    // Fehler trotzdem.
    versprechen.catch(() => {});
    return { bis, offen: true, versprechen, erfuelle, verwirf };
};

const alsUngesichert = (g: Generation | undefined): UngesicherterStand | undefined =>
    g?.gescheitert ? { save: g.save, fehler: g.fehler } : undefined;

/** Die jüngste gescheiterte Generation einer Folge, auf Wunsch nur bis zu einer Grenze. */
const juengsteGescheiterte = (folge: Generation[] | undefined, grenze = Infinity): Generation | undefined => {
    if (!folge) return undefined;
    for (let i = folge.length - 1; i >= 0; i--) {
        if (folge[i].id <= grenze && folge[i].gescheitert) return folge[i];
    }
    return undefined;
};

export const erzeugeSchreibbuendel = (opt: SchreibbuendelOptionen): Schreibbuendel => {
    const ruheMs = opt.ruheMs ?? LOKALE_SICHERUNG_RUHE_MS;
    const maxWartenMs = opt.maxWartenMs ?? LOKALE_SICHERUNG_MAX_WARTEN_MS;
    /** Je Eintrag die Generationen, älteste zuerst. */
    const folgen = new Map<string, Generation[]>();
    let laufendeNummer = 0;
    let letzteGeneration = 0;
    // Jeder Vorgang wartet auf den vorigen. Die IndexedDB ordnet
    // readwrite-Transaktionen zwar selbst nach ihrer Erzeugung, aber nur,
    // solange niemand zwischen Aufruf und Transaktion ein `await` einbaut —
    // die Kette hält die Reihenfolge des AUFRUFS fest. Sie wirft nie (Fehler
    // gehen an die Versprechen der Aufrufer), damit ein gescheiterter Vorgang
    // die folgenden nicht mitreisst.
    let kette: Promise<void> = Promise.resolve();
    let zuletztBeschaeftigt = false;

    const istBeschaeftigt = () => folgen.size > 0;

    const meldeZustand = () => {
        const jetzt = istBeschaeftigt();
        if (jetzt === zuletztBeschaeftigt) return;
        zuletztBeschaeftigt = jetzt;
        try {
            opt.beiBeschaeftigung?.(jetzt);
        } catch {
            // Siehe `beiBeschaeftigung`.
        }
    };

    const stelleAn = <T>(arbeit: () => Promise<T>): Promise<T> => {
        const lauf = kette.then(arbeit);
        kette = lauf.then(() => undefined, () => undefined);
        return lauf;
    };

    const loescheUhren = (g: Generation) => {
        opt.loescheUhr(g.ruheUhr);
        opt.loescheUhr(g.maxUhr);
        g.ruheUhr = undefined;
        g.maxUhr = undefined;
    };

    const istErledigt = (g: Generation) =>
        !g.angestellt && !g.laeuft && !g.gescheitert && g.nummer <= g.gesichertBis && g.fenster.length === 0;

    /** Erledigte Generationen gehen; der Eintrag geht, wenn keine mehr übrig ist. */
    const raeumeAuf = (schluessel: string) => {
        const folge = folgen.get(schluessel);
        if (!folge) return;
        const rest = folge.filter(g => !istErledigt(g));
        for (const g of folge) if (!rest.includes(g)) loescheUhren(g);
        if (rest.length === 0) folgen.delete(schluessel);
        else folgen.set(schluessel, rest);
    };

    /**
     * Nach dem Abschluss einer Generation (geglückt oder gescheitert) sind alle
     * ÄLTEREN desselben Eintrags überholt: was dort noch als ungesichert
     * steht, steckt im jüngeren, vollständigen Bericht — geglückt liegt es
     * fest, gescheitert steht der jüngere als ungesichert da. Ein Stehenlassen
     * hiesse, dass eine spätere Wiederholung Älteres über Neueres schreibt.
     */
    const verwirfAeltere = (schluessel: string, g: Generation, geglueckt: boolean, fehler: unknown) => {
        const folge = folgen.get(schluessel);
        if (!folge) return;
        const index = folge.indexOf(g);
        if (index <= 0) return;
        for (const alt of folge.slice(0, index)) {
            loescheUhren(alt);
            const offene = alt.fenster;
            alt.fenster = [];
            for (const f of offene) {
                if (geglueckt) f.erfuelle();
                else f.verwirf(fehler);
            }
        }
        folgen.set(schluessel, folge.slice(index));
    };

    /** Einen Schreibvorgang ausführen — mit dem Inhalt, der JETZT der jüngste dieser Generation ist. */
    const fuehreAus = async (schluessel: string, g: Generation): Promise<void> => {
        g.angestellt = false;
        if (!folgen.get(schluessel)?.includes(g)) return;
        if (g.nummer <= g.gesichertBis) {
            // Überholt: ein früherer Vorgang hat diesen oder einen neueren
            // Inhalt dieser Generation schon geschrieben.
            raeumeAuf(schluessel);
            meldeZustand();
            return;
        }
        const nummer = g.nummer;
        const save = g.save;
        // Wer ab jetzt noch in diese Generation einreiht, wartet auf den
        // NÄCHSTEN Schreibvorgang — und bringt den Ausgangsstand mit.
        for (const f of g.fenster) f.offen = false;
        g.basisVomNaechsten = true;
        g.laeuft = true;
        let fehler: unknown;
        let geglueckt = true;
        try {
            await opt.schreibe(save);
        } catch (roh) {
            geglueckt = false;
            fehler = fehlerOderErsatz(roh);
        }
        g.laeuft = false;
        const erledigt = g.fenster.filter(f => f.bis <= nummer);
        g.fenster = g.fenster.filter(f => f.bis > nummer);
        if (geglueckt) {
            g.gesichertBis = nummer;
            g.gescheitert = false;
            g.fehler = undefined;
        } else {
            // Kein Zeitgeber: erneut versucht wird beim nächsten Anlass,
            // nicht im Takt, der bei vollem Speicher nur Megabytes
            // umkopierte. Kam während des Schreibens ein neuerer Anschlag in
            // diese Generation, hat DER seine Zeitgeber und läuft weiter.
            g.gescheitert = true;
            g.fehler = fehler;
        }
        verwirfAeltere(schluessel, g, geglueckt, fehler);
        raeumeAuf(schluessel);
        meldeZustand();
        for (const f of erledigt) {
            if (geglueckt) f.erfuelle();
            else f.verwirf(fehler);
        }
    };

    const starte = (schluessel: string, g: Generation) => {
        loescheUhren(g);
        if (g.angestellt) return;
        g.angestellt = true;
        void stelleAn(() => fuehreAus(schluessel, g));
    };

    /** Braucht diese Generation einen Schreibvorgang, und ist sie nicht schon überholt? */
    const mussGeschriebenWerden = (folge: Generation[], g: Generation): boolean => {
        if (g.nummer <= g.gesichertBis) return false;
        // Eine gescheiterte ältere Generation wird nicht mehr versucht, wenn
        // eine jüngere dasteht — die ersetzt sie (Regel oben).
        return !(g.gescheitert && folge[folge.length - 1] !== g);
    };

    /**
     * Die Grenze eines Zugriffs: jede offene Generation schliessen und alles,
     * was vor dem Zugriff eingereiht war, VOR ihm anstellen.
     */
    const schliesseUndStelleAn = (): number => {
        const grenze = letzteGeneration;
        for (const [schluessel, folge] of Array.from(folgen.entries())) {
            for (const g of folge) {
                g.offen = false;
                if (mussGeschriebenWerden(folge, g)) starte(schluessel, g);
                else loescheUhren(g);
            }
        }
        return grenze;
    };

    const sichtBis = (grenze: number): Zugriffssicht => ({
        ungesichert: (userId, projectId) =>
            alsUngesichert(juengsteGescheiterte(folgen.get(eintragsSchluessel(userId, projectId)), grenze)),
        alleUngesicherten: () => Array.from(folgen.values())
            .map(folge => alsUngesichert(juengsteGescheiterte(folge, grenze)))
            .filter((u): u is UngesicherterStand => !!u),
        verwirfBisZurGrenze: (userId, projectId) => {
            const schluessel = eintragsSchluessel(userId, projectId);
            const folge = folgen.get(schluessel);
            if (!folge) return;
            const danach: Generation[] = [];
            for (const g of folge) {
                if (g.id > grenze) { danach.push(g); continue; }
                // Eine hinter dem Zugriff schon angestellte Wiederholung
                // findet ihre Generation nicht mehr und tut nichts
                // (`fuehreAus` prüft die Folge).
                loescheUhren(g);
                const wartende = g.fenster;
                g.fenster = [];
                for (const f of wartende) f.erfuelle();
            }
            if (danach.length > 0) folgen.set(schluessel, danach);
            else folgen.delete(schluessel);
            meldeZustand();
        },
    });

    const einreihen = (save: IncomingSave): Promise<void> => {
        const schluessel = eintragsSchluessel(save.userId, save.projectId);
        const nummer = ++laufendeNummer;
        // Ein anderes Projekt wartet noch: JETZT schreiben. Sonst läge beim
        // Projektwechsel der letzte Stand von A bis zu 2 s nur im Speicher,
        // während B schon bearbeitet wird. Ein dort GESCHEITERTER Stand wird
        // hier nicht mitgenommen — das wären bei vollem Speicher wieder
        // Megabytes je Anschlag; ihn holen die übrigen Anlässe.
        for (const [anderer, folge] of Array.from(folgen.entries())) {
            if (anderer === schluessel) continue;
            for (const g of folge) {
                if (!g.gescheitert && g.nummer > g.gesichertBis) starte(anderer, g);
            }
        }
        const folge = folgen.get(schluessel) ?? [];
        let g = folge[folge.length - 1];
        if (!g || !g.offen) {
            // Neue Generation: eigener Inhalt, eigener Ausgangsstand, wie beim
            // ersten Einreihen.
            g = {
                id: ++letzteGeneration, offen: true,
                save, nummer, gesichertBis: 0, angestellt: false, laeuft: false,
                gescheitert: false, fehler: undefined, basisVomNaechsten: false,
                ruheUhr: undefined, maxUhr: undefined, fenster: [],
            };
            folge.push(g);
            folgen.set(schluessel, folge);
        } else {
            // Neuester Inhalt; Ausgangsstand des ERSTEN Aufrufs seit Beginn
            // des letzten Schreibvorgangs (Regel oben).
            g.save = g.basisVomNaechsten
                ? save
                : { ...save, base: g.save.base, baseUpdatedAt: g.save.baseUpdatedAt };
            g.basisVomNaechsten = false;
            g.nummer = nummer;
        }
        const letztes = g.fenster[g.fenster.length - 1];
        let fenster: Fenster;
        if (letztes && letztes.offen) {
            letztes.bis = nummer;
            fenster = letztes;
        } else {
            fenster = neuesFenster(nummer);
            g.fenster.push(fenster);
        }
        const gen = g;
        opt.loescheUhr(gen.ruheUhr);
        gen.ruheUhr = opt.setzeUhr(() => { if (folgen.get(schluessel)?.includes(gen)) starte(schluessel, gen); }, ruheMs);
        // Die Obergrenze läuft ab dem ERSTEN Aufruf seit dem letzten
        // Anstellen und wird nie verlängert — wer ohne Pause tippt, wird
        // trotzdem alle 2 s gesichert.
        if (gen.maxUhr === undefined) {
            gen.maxUhr = opt.setzeUhr(() => { if (folgen.get(schluessel)?.includes(gen)) starte(schluessel, gen); }, maxWartenMs);
        }
        meldeZustand();
        return fenster.versprechen;
    };

    const sofort = (save: IncomingSave): Promise<void> => {
        const versprechen = einreihen(save);
        const schluessel = eintragsSchluessel(save.userId, save.projectId);
        const folge = folgen.get(schluessel);
        const g = folge?.[folge.length - 1];
        if (g) starte(schluessel, g);
        return versprechen;
    };

    const nachDemWartenden = <T>(arbeit: (sicht: Zugriffssicht) => Promise<T>): Promise<T> => {
        const grenze = schliesseUndStelleAn();
        return stelleAn(() => arbeit(sichtBis(grenze)));
    };

    const schreibeJetzt = async (): Promise<void> => {
        const offen = await nachDemWartenden(async sicht => sicht.alleUngesicherten()[0]);
        if (offen) throw offen.fehler;
    };

    return {
        einreihen,
        sofort,
        nachDemWartenden,
        schreibeJetzt,
        istBeschaeftigt,
        ungesichert: (userId, projectId) =>
            alsUngesichert(juengsteGescheiterte(folgen.get(eintragsSchluessel(userId, projectId)))),
    };
};

const errorStatus = (error: unknown): number | undefined => {
    const status = (error as { status?: unknown })?.status;
    return typeof status === 'number' ? status : undefined;
};

const errorText = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    const message = (error as { message?: unknown })?.message;
    return typeof message === 'string' ? message : String(error ?? '');
};

const errorCode = (error: unknown): string | undefined => {
    const code = (error as { code?: unknown } | null | undefined)?.code;
    return typeof code === 'string' ? code : undefined;
};

/**
 * Hat die DATENBANK den Inhalt abgelehnt — so, dass derselbe Inhalt bei
 * jedem Versuch genauso scheitert?
 *
 * Erkannt am SQLSTATE der PostgREST-Antwort (`code`, fünf Zeichen). Ein
 * `status` kommt bei supabase-js nicht mit — deshalb liefen diese Absagen
 * bisher als 'retryable' durch: 22007 von Meyers Android (leeres Datum, seit
 * 26.08.2026) hing so wochenlang still in der Warteschlange, alle paar
 * Minuten ein voller Upload, und ging am 07.09.2026 nach dem Fix mit
 * wochenaltem Inhalt durch. Gezählt werden nur Klassen, bei denen Warten
 * nichts ändert:
 *   • 22… Datenfehler (Datum, Zahl, Textlänge),
 *   • 23… Integrität (eindeutig, Fremdschlüssel, Pflichtfeld, Prüfregel),
 *   • 42501 fehlendes Recht (auch RLS),
 *   • P0001 eigene Ausnahme eines Triggers (Riegel). BAUPHASE und
 *     BUILD_VERALTET sind ebenfalls P0001 und werden VORHER abgefangen.
 * Bewusst NICHT: 40… (Serialisierung, Deadlock), 57… (Abbruch, Zeitlimit),
 * 08…/53… (Verbindung, Ressourcen) — das heilt die Wiederholung; 42703/42P01/
 * 42883/PGRST… (Spalte, Tabelle, Funktion fehlt) — das heilt die Migration,
 * die gerade noch nicht gelaufen ist.
 */
export const istDatenbankAbsage = (error: unknown): boolean => {
    const code = errorCode(error);
    if (!code || code.length !== 5) return false;
    return code.startsWith('22') || code.startsWith('23') || code === '42501' || code === 'P0001';
};

/**
 * Reihenfolge der Pruefungen, und warum sie so ist:
 *   1. `QuotaExceededError` — ein lokales Speicherproblem, das mit dem
 *      Server nichts zu tun hat.
 *   2. Build-Riegel. VOR der Sitzungspruefung: ein 401 mit dem Signalwort
 *      gewinnt fuer den Riegel, weil der Server ihn AUSDRUECKLICH nennt,
 *      waehrend 'auth-required' nur aus einem Statuscode geraten waere.
 *      Praktisch kostet das nichts: ein erneutes Anmelden wuerde diesem
 *      Bundle ohnehin nicht helfen, und nach dem Reload laeuft die
 *      Anmeldung ohnehin neu an.
 *   3. Bauphase — aufgeschoben, nicht blockiert.
 *   4. Netzfehler — VOR der Sitzungspruefung: ohne Netz liefert getUser()
 *      keinen Nutzer, und das ist keine Abmeldung (netzFehler.ts).
 *   5. Sitzung (401, „nicht eingeloggt", JWT).
 *   6. Absage der Datenbank (`istDatenbankAbsage`) — VOR den übrigen
 *      Statuscodes: sie gilt dem Inhalt, und neuer Inhalt hebt sie auf
 *      (coalesceReportSave), während 'blocked' eingefroren bleibt.
 *   7. Übrige Statuscodes.
 */
export const classifySyncError = (error: unknown): SyncFailureKind => {
    if (error instanceof Error && error.name === 'QuotaExceededError') return 'storage-error';

    // Der Build-Riegel wirft 'BUILD_VERALTET' — ebenfalls eine
    // Postgres-Exception mit HTTP 400. Er unterscheidet sich von der Bauphase
    // darin, dass KEIN Warten hilft: dieses Bundle wird nie wieder schreiben
    // duerfen. Der Eintrag bleibt trotzdem 'pending' und ohne Wartezeit — der
    // Reload holt ihn mit dem neuen Build. VOR dem Statuscheck aus demselben
    // Grund wie die Bauphase: der 400 gewaenne sonst und machte 'blocked'
    // daraus, also ein Abstellgleis fuer Arbeit, die noch hochgehen soll.
    if (isBuildGuardWriteError(error)) return BUILD_OUTDATED_ERROR_CODE;

    // Der Trigger auf `projects` wirft waehrend der Bauphase 'BAUPHASE' — als
    // Postgres-Exception, die PostgREST mit 400 beantwortet. Ohne diese Zeile
    // landete der Eintrag als 'blocked' dauerhaft im Abstellgleis, obwohl er
    // nach der Bauphase ganz normal hochgehen darf. VOR dem Statuscheck, weil
    // der 400 sonst gewinnt.
    if (isMaintenanceWriteError(error)) return 'retryable';

    // Die Speicherwege werfen ohne Netz einen NetzFehler statt „Nicht
    // eingeloggt". Er wird wiederholt, ohne Wartezeit (markSyncFailure).
    // Eingestuft wird der Fehler selbst, nicht der Online-Stand des Geraets;
    // ein 4xx bleibt eine Absage des Servers (istNetzFehler).
    if (istNetzFehler(error)) return 'retryable';

    const status = errorStatus(error);
    const message = errorText(error);

    if (status === 401 || /nicht eingeloggt|jwt/i.test(message)) return 'auth-required';
    if (istDatenbankAbsage(error)) return ABGEWIESEN_ERROR_CODE;
    if (status === 400 || status === 403 || status === 422) return 'blocked';

    return 'retryable';
};

const BACKOFF_MS = [5_000, 15_000, 60_000, 300_000];

export const computeNextRetry = (retryCount: number, nowIso: string, random: () => number): string => {
    const base = BACKOFF_MS[Math.min(retryCount, BACKOFF_MS.length - 1)];
    const jittered = base * (1 + random() * 0.2);
    return new Date(new Date(nowIso).getTime() + Math.round(jittered)).toISOString();
};

export const canFlush = (entry: QueueEntry, sessionUserId: string | null, nowIso: string): boolean => {
    if (!sessionUserId || entry.userId !== sessionUserId) return false;
    if (entry.state !== 'pending') return false;
    if (entry.nextRetryAt && entry.nextRetryAt > nowIso) return false;
    return true;
};

/**
 * Zustand 'syncing' setzen UND festhalten, wann das Hochladen begonnen hat.
 *
 * Der Zeitstempel ist der einzige Weg zurück: stirbt der Tab zwischen dieser
 * Markierung und dem Aufräumen (Reload nach einem Sitzungsentzug, geschlossener
 * Tab, Absturz), bleibt der Eintrag in 'syncing' liegen — und `canFlush` nimmt
 * nur 'pending'. Ohne Zeitstempel wäre nicht zu unterscheiden, ob gerade ein
 * anderer Tab wirklich hochlädt oder ob hier Arbeit für immer festhängt.
 */
export const markSyncStart = (entry: QueueEntry, nowIso: string, flushId: string): QueueEntry =>
    ({ ...entry, state: 'syncing', syncStartedAt: nowIso, flushId });

/** Was `starteFlush` zurueckgibt: der Stand, der hochgeht, und seine Kennung. */
export interface FlushStart {
    /**
     * Der EINZIGE Stand, der hochgeladen werden darf — derselbe, der eben
     * markiert und persistiert wurde. Zwischen dem Lesen vor dem Toerwaechter
     * und dieser Markierung kann der Anwender laengst weitergeschrieben haben
     * (coalesce, rev+1); wer dann den alten Snapshot hochlaedt und mit der
     * neuen Kennung abschliesst, loescht genau diese nie hochgeladene Eingabe.
     */
    eintrag: QueueEntry;
    kennung: AbschlussKennung;
}

/**
 * Einen Flush eroeffnen: markieren, Lauf-Identitaet vergeben — und Inhalt wie
 * Kennung ATOMAR aus demselben Eintrag nehmen.
 *
 * `null`, wenn kein Eintrag mehr da ist (ein anderer Tab war schneller). Dann
 * gibt es nichts hochzuladen; jeder Versuch schriebe einen Stand, den die
 * Warteschlange nicht mehr kennt.
 */
export const starteFlush = (
    aktuell: QueueEntry | undefined,
    nowIso: string,
    flushId: string,
): FlushStart | null => {
    if (!aktuell) return null;
    const eintrag = markSyncStart(aktuell, nowIso, flushId);
    return {
        eintrag,
        kennung: { entryId: eintrag.entryId, flushId: eintrag.flushId, rev: eintrag.rev },
    };
};

/**
 * Wie lange ein 'syncing' höchstens laufen darf, bevor es als verwaist gilt.
 *
 * Fünf Minuten sind reichlich: ein Upload, der so lange braucht, ist entweder
 * tot oder läuft in einen Timeout. Kürzer wäre gefährlich — ein zweiter Tab
 * mit schlechter Verbindung darf nicht aus seinem laufenden Upload geworfen
 * werden (die IndexedDB gehört allen Tabs gemeinsam).
 */
export const MAX_SYNC_RUNTIME_MS = 5 * 60 * 1000;

/**
 * Hängt dieser Eintrag in 'syncing' fest?
 *
 * Ein Eintrag OHNE `syncStartedAt` gilt als verwaist: er stammt aus einem
 * Build vor dieser Änderung und liegt dort seit unbekannter Zeit. Das Risiko
 * ist bewusst asymmetrisch — ein versehentlich zu früh freigegebener Eintrag
 * läuft in Anker und Zusammenführung (`resolveStaleEntry`, `mergeReports`)
 * und kann dort nichts überschreiben, während ein ewig liegender Eintrag
 * schlicht nie hochgeladene Arbeit ist.
 */
export const istVerwaisterSync = (
    entry: QueueEntry,
    nowIso: string,
    maxMs: number = MAX_SYNC_RUNTIME_MS,
): boolean => {
    if (entry.state !== 'syncing') return false;
    if (!entry.syncStartedAt) return true;
    const start = new Date(entry.syncStartedAt).getTime();
    // Unlesbarer Zeitstempel = kein Zeitstempel.
    if (!Number.isFinite(start)) return true;
    return new Date(nowIso).getTime() - start >= maxMs;
};

/**
 * Den verwaisten Eintrag zurück in die Warteschlange geben.
 *
 * NUR der Zustand wird angefasst: Inhalt, Ausgangsstand, Anker, Revision und
 * Fehlerspuren bleiben, wie sie sind — der nächste Flush soll genau diesen
 * Stand mit genau diesem Anker hochladen, sonst verlöre die Freigabe das,
 * was sie retten soll.
 */
export const zurueckInDieWarteschlange = (entry: QueueEntry): QueueEntry => {
    const { syncStartedAt: _start, flushId: _lauf, ...ohneLauf } = entry;
    // `entryId` bleibt ausdrücklich stehen: das ist kein neuer Eintrag,
    // sondern derselbe, der wieder wartet. Die Spuren des LAUFS müssen dagegen
    // weg — sonst gälte der Eintrag weiter als von diesem Lauf belegt, und ein
    // verspäteter Abschluss desselben Laufs träfe ihn erneut.
    return { ...ohneLauf, state: 'pending' };
};

/**
 * Den verwaisten Eintrag zurück in die Warteschlange geben.
 *
 * NUR der Zustand und die Spuren des abgebrochenen Laufs werden angefasst:
 * Inhalt, Ausgangsstand, Anker, Revision und Fehlerspuren bleiben, wie sie
 * sind — der nächste Flush soll genau diesen Stand mit genau diesem Anker
 * hochladen, sonst verlöre die Freigabe das, was sie retten soll.
 */
export const releaseOrphanedSync = zurueckInDieWarteschlange;

/**
 * Was ein Flush sich merken muss, um seinen eigenen Abschluss zuzuordnen.
 * Beides zusammen zeigt auf genau den Stand, der hochgeladen wurde.
 */
export interface AbschlussKennung {
    entryId?: string;
    /** Die Identitaet DIESES Laufs — unterscheidet zwei Laeufe am selben Eintrag. */
    flushId?: string;
    rev: number;
}

/**
 * Darf dieser Abschluss den aktuellen Eintrag überhaupt anfassen?
 *
 * Nein, sobald inzwischen ein ANDERER Eintrag in der Warteschlange liegt —
 * das ist der Fall nach „gelöscht und neu angelegt". Ein verspäteter
 * Abschluss (Erfolg wie Fehlschlag) würde dort sonst fremde, nie hochgeladene
 * Arbeit löschen oder ihr einen Fehlerzustand samt Wartezeit anhängen.
 *
 * Trägt eine der beiden Seiten keine Identität (Bestandsdaten aus Builds vor
 * dieser Änderung), bleibt es beim alten Verhalten: anfassen ja, löschen nur
 * bei gleicher `rev`. Mehr ist über einen solchen Eintrag nicht zu wissen.
 */
export const darfAbschlussAnwenden = (
    aktuell: QueueEntry | undefined,
    geflusht: AbschlussKennung,
): boolean => {
    if (!aktuell) return false;
    // Der LAUF muss derselbe sein, und zwar streng: trägt der Eintrag keine
    // Lauf-Identität mehr (freigegeben, abgeschlossen), passt kein Abschluss,
    // der eine mitbringt. Zwei Läufe ohne Identität sind Bestandsdaten und
    // verhalten sich wie früher.
    if ((aktuell.flushId ?? null) !== (geflusht.flushId ?? null)) return false;
    if (!aktuell.entryId || !geflusht.entryId) return true;
    return aktuell.entryId === geflusht.entryId;
};

/**
 * Darf dieser Abschluss den Eintrag LÖSCHEN?
 *
 * Nur derselbe Eintrag und nur dieselbe Revision: eine während des Hochladens
 * dazugekommene Eingabe (rev+1) muss liegen bleiben und beim nächsten Lauf
 * hochgehen.
 */
export const darfAbschlussLoeschen = (
    aktuell: QueueEntry | undefined,
    geflusht: AbschlussKennung,
): boolean => !!aktuell && darfAbschlussAnwenden(aktuell, geflusht) && aktuell.rev === geflusht.rev;

export const markSyncFailure = (
    entry: QueueEntry,
    kind: SyncFailureKind,
    error: unknown,
    nowIso: string,
    random: () => number,
): QueueEntry => {
    // Der Lauf ist vorbei — seine Spuren gehen mit, sonst gälte der Eintrag
    // weiter als von diesem Lauf belegt (flushId) und `istVerwaisterSync`
    // rechnete mit einem Start, den es nicht mehr gibt.
    const { syncStartedAt: _start, flushId: _lauf, ...ohneLauf } = entry;
    // Bei einer Absage der Datenbank steht der SQLSTATE vorn — er ist das
    // Erste, was man zur Diagnose braucht, und der Balken zeigt den Text.
    const code = kind === 'abgewiesen' ? errorCode(error) : undefined;
    const base: QueueEntry = {
        ...ohneLauf,
        lastErrorCode: kind,
        lastErrorMessage: code ? `${code}: ${errorText(error)}` : errorText(error),
    };

    // Der NetzFehler des getUser-Wächters wartet wie vorher als
    // 'auth-required': kein Timer, `retryCount` bleibt. Mit Backoff wüchse die
    // Wartezeit im Funkloch auf 5–6 Minuten, und 'online' wie „Tab sichtbar"
    // liefen an canFlush vorbei, obwohl das Netz längst wieder da ist.
    if (kind === 'retryable' && istEigenerNetzFehler(error)) {
        return { ...base, state: 'pending', nextRetryAt: null };
    }

    switch (kind) {
        case 'blocked':
            return { ...base, state: 'blocked', nextRetryAt: null };
        case 'auth-required':
            // Kein Timer: der nächste Flush wird durch Login/Session-Recovery ausgelöst.
            return { ...base, state: 'pending', nextRetryAt: null };
        case 'build-veraltet':
            // Wie 'auth-required' und aus demselben Grund: ein Wiederholungs-
            // timer wäre eine Lüge. Dieses Bundle darf nicht mehr schreiben,
            // und `istBuildVeraltet()` hält den Flush ohnehin an
            // (App.tsx: `{ upload: false, reason: 'build-veraltet' }`). Der
            // Eintrag bleibt 'pending' und geht nach dem Reload mit dem neuen
            // Build hoch — nicht 'blocked', das wäre ein Abstellgleis.
            // `retryCount` bleibt unangetastet: der Versuch war nicht am
            // Eintrag gescheitert, und er soll den Altersriegel nicht näher
            // rücken.
            return { ...base, state: 'pending', nextRetryAt: null };
        case 'abgewiesen':
            // Kein Timer: derselbe Inhalt scheitert gleich wieder, und jeder
            // Versuch lädt den ganzen Bericht hoch. 'blocked', damit kein
            // Durchlauf ihn anfasst — aber nicht eingefroren: neuer Inhalt
            // (coalesceReportSave) und der nächste Start der App
            // (abgewiesenErneutVersuchen) schicken ihn erneut.
            return { ...base, state: 'blocked', nextRetryAt: null };
        default:
            return {
                ...base,
                state: 'pending',
                retryCount: entry.retryCount + 1,
                nextRetryAt: computeNextRetry(entry.retryCount, nowIso, random),
            };
    }
};
